import { Extension } from '@tiptap/core';
import { Slice } from '@tiptap/pm/model';
import { NodeSelection, Plugin, TextSelection } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';

import { DragAutoScroll } from './auto-scroll';
import {
	DROP_CURSOR_EDGE_INSET_PX,
	DROP_CURSOR_IDLE_TIMEOUT_MS,
	RTE_DRAGGING_CLASS,
} from './constants';
import { DropCursorView, Point } from './drop-cursor-view';

type Drag = NonNullable<EditorView['dragging']>;

type Drop = { slice: Slice; move: boolean; source: NodeSelection | null };

/** The dragged node's own selection: undeclared by the types, but a move without it deletes the caret. */
const sourceOf = (dragging: Drag): NodeSelection | null => {
	const { node } = dragging as Drag & { node?: unknown };
	return node instanceof NodeSelection ? node : null;
};

/** Owns the drag listeners and feeds both the drop indicator and edge auto-scrolling. */
class DragFeedback {
	private cursor: DropCursorView;
	private autoScroll: DragAutoScroll;
	private pointer: Point | null = null;
	private engaged = false;
	private idleTimeout = 0;
	private teardown: Array<() => void> = [];

	constructor(private view: EditorView) {
		this.cursor = new DropCursorView(view);
		this.autoScroll = new DragAutoScroll(view.dom, () => this.follow());

		// Armed from the first sign of a drag, because an embed under the pointer hides every later one.
		this.listen(document, 'dragstart', () => this.arm());
		this.listen(document, 'dragenter', () => this.arm());

		// Tracking on `document`, not the editor, keeps a pointer parked on a sticky header scrolling.
		this.listen(view.dom, 'dragover', (event) => this.claim(event));
		this.listen(document, 'dragover', (event) => this.track(event));
		this.listen(document, 'dragend', () => this.end());
		this.listen(document, 'drop', (event) => this.finish(event));
	}

	destroy() {
		clearTimeout(this.idleTimeout);
		document.body.classList.remove(RTE_DRAGGING_CLASS);
		this.teardown.forEach((off) => off());
		this.autoScroll.stop();
		this.cursor.destroy();
	}

	/** A doc change mid-drag moves the content under the pointer, so re-resolve the position. */
	refresh() {
		if (this.cursor.isVisible) this.follow();
	}

	/** Drops on the editor: the line wins over the position ProseMirror re-derives from the pointer. */
	handleEditorDrop(event: DragEvent, slice: Slice, move: boolean): boolean {
		const dragging = this.view.dragging;
		const target = this.cursor.targetPos;

		if (!dragging || target === null) return false;

		event.preventDefault();
		this.applyDrop({ slice, move, source: sourceOf(dragging) }, target);
		this.end();

		return true;
	}

	private listen(target: EventTarget, name: string, handler: (event: DragEvent) => void) {
		const listener = (event: Event) => handler(event as DragEvent);
		target.addEventListener(name, listener);
		this.teardown.push(() => target.removeEventListener(name, listener));
	}

	/** Lets drag events reach the editor through any iframe, and keeps the idle watchdog fed. */
	private arm() {
		if (!this.view.editable) return;

		document.body.classList.add(RTE_DRAGGING_CLASS);
		clearTimeout(this.idleTimeout);
		this.idleTimeout = window.setTimeout(() => this.end(), DROP_CURSOR_IDLE_TIMEOUT_MS);
	}

	private claim(event: DragEvent) {
		if (!this.view.editable) return;

		// Without this the browser refuses the drop and never treats the editor as a target.
		event.preventDefault();
		this.engaged = true;
	}

	/** Ignores drags that never touched the editor, so unrelated page drags don't scroll it. */
	private track(event: DragEvent) {
		if (!this.engaged || !this.view.editable) return;

		this.pointer = { x: event.clientX, y: event.clientY };
		this.follow();
		this.autoScroll.update(event.clientY);

		// Off the editor the browser refuses the drop, so claim it here and honour the line ourselves.
		if (this.view.dragging && !this.isOverEditor(event.target)) {
			event.preventDefault();
			if (event.dataTransfer) {
				event.dataTransfer.dropEffect = this.view.dragging.move ? 'move' : 'copy';
			}
		}

		// A drag that leaves the window never reports back, so stop waiting for it eventually.
		clearTimeout(this.idleTimeout);
		this.idleTimeout = window.setTimeout(() => this.end(), DROP_CURSOR_IDLE_TIMEOUT_MS);
	}

	private follow() {
		if (this.pointer) this.cursor.showAt(this.clampToEditor(this.pointer));
	}

	/** A pointer outside the editor's visible slice resolves against the nearest visible edge. */
	private clampToEditor({ x, y }: Point): Point {
		const rect = this.view.dom.getBoundingClientRect();
		const inset = DROP_CURSOR_EDGE_INSET_PX;
		const top = Math.max(rect.top, 0) + inset;
		const bottom = Math.min(rect.bottom, window.innerHeight) - inset;

		return {
			x: Math.min(Math.max(x, rect.left + inset), rect.right - inset),
			y: Math.min(Math.max(y, top), Math.max(bottom, top)),
		};
	}

	/** Any editor, not just ours: a page can hold several, and each handles its own drop. */
	private isOverEditor(target: EventTarget | null): boolean {
		return target instanceof Element && !!target.closest('.ProseMirror');
	}

	/** Drops off every editor: `handleEditorDrop` never runs for these, so catch them here. */
	private finish(event: DragEvent) {
		const dragging = this.view.dragging;
		const target = this.cursor.targetPos;

		if (dragging && target !== null && !this.isOverEditor(event.target)) {
			event.preventDefault();
			this.applyDrop({ ...dragging, source: sourceOf(dragging) }, target);
		}

		this.end();
	}

	/** Mirrors `prosemirror-view`'s own drop handling, minus the position it can no longer read. */
	private applyDrop({ slice, move, source }: Drop, insertAt: number) {
		const tr = this.view.state.tr;

		if (move) {
			if (source) source.replace(tr);
			else tr.deleteSelection();
		}

		const pos = tr.mapping.map(insertAt);
		const single =
			slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1
				? slice.content.firstChild
				: null;
		const before = tr.doc;

		if (single) tr.replaceRangeWith(pos, pos, single);
		else tr.replaceRange(pos, pos, slice);

		this.view.dragging = null;
		if (tr.doc.eq(before)) return;

		const $start = tr.doc.resolve(pos);
		const dropped = $start.nodeAfter;
		// Mapping `insertAt` again — now through the replace — lands on the end of what went in.
		tr.setSelection(
			single && dropped && NodeSelection.isSelectable(dropped)
				? new NodeSelection($start)
				: TextSelection.between($start, tr.doc.resolve(tr.mapping.map(insertAt)))
		);

		this.view.dispatch(tr.scrollIntoView());
		this.view.focus();
	}

	private end() {
		clearTimeout(this.idleTimeout);
		document.body.classList.remove(RTE_DRAGGING_CLASS);
		this.engaged = false;
		this.pointer = null;
		this.autoScroll.stop();
		this.cursor.hide();
	}
}

/** Replaces StarterKit's `dropcursor`, which is disabled where StarterKit is configured. */
export const RteDropCursor = Extension.create({
	name: 'rteDropCursor',

	addProseMirrorPlugins() {
		// One editor, one instance: `view` builds it and `handleDrop` needs the same one.
		let feedback: DragFeedback | null = null;

		return [
			new Plugin({
				view: (view) => {
					feedback = new DragFeedback(view);

					return {
						update: (updatedView, prevState) => {
							if (prevState.doc !== updatedView.state.doc) feedback?.refresh();
						},
						destroy: () => {
							feedback?.destroy();
							feedback = null;
						},
					};
				},
				props: {
					// Returns false for file drops (no `dragging` slice), leaving them to `RteImage`.
					handleDrop: (_view, event, slice, moved) =>
						feedback?.handleEditorDrop(event, slice, moved) ?? false,
				},
			}),
		];
	},
});
